import React from 'react';
import PropTypes from 'prop-types';
import { renderSquares, renderProgressBar, formatData, StackedBarChartHorizontal} from './RenderUtils';
import { Card, ListGroup, Button } from 'react-bootstrap';
import './MaterialStatCard.css';

const MaterialStatCard = ({ material, type, calculatorUrl, link_bouwdetail }) => {
  const initialCost = material["cost_initial "] ?? 0;
  // prefer material-specific URL, fallback to top-level prop, then hardcoded default
  const calculatorHref = (material && material.calculatorUrl) || calculatorUrl || "https://tst-buildingbalance.productivity.nl/";
  const linkBouwHref = (material && material.link_bouwdetail) || link_bouwdetail || "https://nkbb.org/thema/bouwdetails/#nkbb_doc_grid";
  return (
    <Card className={`material-stat-card ${type}`} style={{ cursor: "pointer" }}>
      <Card.Img variant="top" src={material?.image ?? '/images/default.webp'} alt={material?.name ?? 'Material Image'} />
      <Card.Body>
        <Card.Subtitle>{type === "traditional" ? "Traditioneel" : "BioBased"}</Card.Subtitle>
        <Card.Title>{material?.name ?? 'Unnamed Material'}</Card.Title>
        <Card.Text>{material?.description ?? 'No description available.'}</Card.Text>
      </Card.Body>

      {/* ListGroup: left = label (with optional dot), right = metric */}
      <ListGroup variant="flush">
        <ListGroup.Item>
          <div className="stat-left"><span className="stat-dot" aria-hidden="true" />Initiele kosten</div>
          <div className="stat-right">{renderSquares(type, initialCost, material)}</div>
        </ListGroup.Item>

        <ListGroup.Item>
          <div className="stat-left"><span className="stat-dot" aria-hidden="true" />Onderhoudskosten</div>
          <div className="stat-right">{renderSquares(type, material?.cost_maintenance, material)}</div>
        </ListGroup.Item>

        <ListGroup.Item>
          <div className="stat-left"><span className="stat-dot" aria-hidden="true" />Beschikbaarheid</div>
          <div className="stat-right">{renderSquares(type, material?.qualities_beschikbaarheid, material)}</div>
        </ListGroup.Item>

        <ListGroup.Item>
          <div className="stat-left"><span className="stat-dot" aria-hidden="true" />Gezondheid</div>
          <div className="stat-right">{renderSquares(type, material?.qualities_circulariteit_gezondheid, material)}</div>
        </ListGroup.Item>

        <ListGroup.Item>
          <div className="stat-left"><span className="stat-dot" aria-hidden="true" />Impact Bewoner</div>
          <div className="stat-right">{renderSquares(type, material?.qualities_impact_bewoner, material)}</div>
        </ListGroup.Item>

        {/* Percentage Biobased: label above progress (full-width) */}
        <ListGroup.Item className="stat-full">
          <div className="stat-left-title">Percentage Biobased</div>
          <div className="stat-widget progress-right">{renderProgressBar(type, material?.qualities_percentage_biobased)}</div>
        </ListGroup.Item>

        <ListGroup.Item className="stat-full">
          <div className="stat-left-title">Herkomst</div>
          <div className="stat-widget small-chart"><StackedBarChartHorizontal data={formatData("Herkomst", material)} type="Herkomst" /></div>
        </ListGroup.Item>

        <ListGroup.Item className="stat-full">
          <div className="stat-left-title">Circulariteit</div>
          <div className="stat-widget small-chart"><StackedBarChartHorizontal data={formatData("Circulariteit", material)} type="Circulariteit" /></div>
        </ListGroup.Item>

        <ListGroup.Item>
          <div className="stat-left">Losmaakbaarheid</div>
          <div className="stat-right progress-right">{renderProgressBar(type, material?.qualities_losmaakbaarheid)}</div>
        </ListGroup.Item>

        <ListGroup.Item>
          <div className="stat-left">Opname NMD</div>
          <div className="stat-right">{!(material?.qualities_opname_nmd?.[0]) ? <span className="status-badge yes">Ja</span> : <span className="status-badge no">Nee</span>}</div>
        </ListGroup.Item>

        <ListGroup.Item>
          <div className="stat-left">CO2 Saldo</div>
          <div className="stat-right"><div className="climate-tag">{material?.milieu_impact_co2_saldo ?? '-'} per m²</div></div>
        </ListGroup.Item>

        <ListGroup.Item>
          <div className="stat-left">MKI</div>
          <div className="stat-right"><div className="climate-tag">{material?.milieu_impact_mki ?? '-'}</div></div>
        </ListGroup.Item>

        <ListGroup.Item>
          <div className="stat-left">CO2 Reductie</div>
          <div className="stat-right"><div className="climate-tag">{material?.milieu_impact_co2_reductie ?? '-'}</div></div>
        </ListGroup.Item>
      </ListGroup>

      <Card.Footer className="material-footer">
        <Button
          as="a"
          href={calculatorHref}
          target="_blank"
          rel="noopener noreferrer"
          className="minimal-btn calculator-btn"
          variant="link"
          aria-label="Open Biobased Calculator"
          title="Open Biobased Calculator"
        >
          <svg className="icon icon-calculator" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <rect x="6" y="2" width="12" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M6 8h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M9 12h.01M12 12h.01M15 12h.01M9 15h.01M12 15h.01M15 15h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span className="btn-label">Calculator</span>
        </Button>

        <Button
          as="a"
          href={linkBouwHref}
          target="_blank"
          rel="noopener noreferrer"
          className="minimal-btn bouwdetail-btn"
          variant="link"
          aria-label="Open Bouwdetails"
          title="Bouwdetails"
        >
          <svg className="icon icon-building" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path d="M3 21h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
            <path d="M7 21V9h4v12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
            <path d="M13 21V5h4v16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
            <path d="M9 10h2M9 13h2M9 16h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span className="btn-label">Bouwdetails</span>
        </Button>
      </Card.Footer>
    </Card>
  );
};

MaterialStatCard.propTypes = {
  material: PropTypes.shape({
    image: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    "cost_initial ": PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cost_maintenance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_beschikbaarheid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_gezondheid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_impact_bewoner: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_percentage_biobased: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_losmaakbaarheid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_opname_nmd: PropTypes.array,
    milieu_impact_co2_saldo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    milieu_impact_mki: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    milieu_impact_co2_reductie: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_circulariteit_gezondheid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_herkomst_nieuw: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_herkomst_biobased: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_herkomst_gerecycled: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_herkomst_hergebruikt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_circulariteit_stort: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_circulariteit_verbranden: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_circulariteit_recyclen: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    qualities_circulariteit_hergebruiken: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    calculatorUrl: PropTypes.string,
    link_bouwdetail: PropTypes.string,
  }),
  calculatorUrl: PropTypes.string,
  link_bouwdetail: PropTypes.string,
  type: PropTypes.string.isRequired,
};

export default MaterialStatCard;

// Helper function to update Firebase with local image paths
export const updateMaterialImages = async (db) => {
  try {
    await MaterialStatCard.updateFirebaseImages(db);
    console.log('Material images updated successfully');
  } catch (error) {
    console.error('Error updating material images:', error);
  }
};

// default props
MaterialStatCard.defaultProps = {
  calculatorUrl: "https://tst-buildingbalance.productivity.nl/",
  link_bouwdetail: "https://nkbb.org/thema/bouwdetails/#nkbb_doc_grid"
};
